import { fabric } from "fabric";
import { SHAPE_PATHS, SHAPE_POINTS } from "./shapes.jsx";

// Shared maths for layer gradients, dashed strokes and image frames, used by
// the editor (fabric), the thumbnails (TemplatePreview) and mirrored in the
// backend render engine (_gradient_vector, _dasharray, _mask_css) so all three
// draw the same thing.

// Photo adjustments as a CSS filter, the same string the canvas and the server
// (render_engine._image_filter) use. Empty when there's nothing to apply.
export const IMAGE_ADJUST_DEFAULTS = { brightness: 100, contrast: 100, saturation: 100, hue: 0, grayscale: 0, sepia: 0, blur: 0 };

export function imageAdjustFilter(adjust, blurScale = 1) {
  if (!adjust) return "";
  const a = { ...IMAGE_ADJUST_DEFAULTS, ...adjust };
  return (
    `brightness(${a.brightness}%) contrast(${a.contrast}%) saturate(${a.saturation}%) ` +
    `hue-rotate(${a.hue}deg) grayscale(${a.grayscale}%) sepia(${a.sepia}%) blur(${a.blur * blurScale}px)`
  );
}

// Unit-box vector for a CSS-style angle (0 = towards the top, clockwise).
export function gradientVector(angle) {
  const a = ((Number(angle) || 0) * Math.PI) / 180;
  const dx = Math.sin(a) / 2;
  const dy = -Math.cos(a) / 2;
  return { x1: 0.5 - dx, y1: 0.5 - dy, x2: 0.5 + dx, y2: 0.5 + dy };
}

export const DEFAULT_FILL_GRADIENT = {
  kind: "linear",
  color_start: "#00A69C",
  color_end: "#293A8A",
  angle: 135,
};

// fabric.Gradient across the object's own box ('percentage' units).
export function fabricGradient(spec) {
  const colorStops = [
    { offset: 0, color: spec.color_start },
    { offset: 1, color: spec.color_end },
  ];
  if (spec.kind === "radial") {
    return new fabric.Gradient({
      type: "radial",
      gradientUnits: "percentage",
      coords: { x1: 0.5, y1: 0.5, r1: 0, x2: 0.5, y2: 0.5, r2: 0.5 },
      colorStops,
    });
  }
  return new fabric.Gradient({
    type: "linear",
    gradientUnits: "percentage",
    coords: gradientVector(spec.angle),
    colorStops,
  });
}

export function svgGradientDef(id, spec) {
  const stops = [
    <stop key="0" offset="0" stopColor={spec.color_start} />,
    <stop key="1" offset="1" stopColor={spec.color_end} />,
  ];
  if (spec.kind === "radial") {
    return (
      <defs>
        <radialGradient id={id} cx=".5" cy=".5" r=".5">{stops}</radialGradient>
      </defs>
    );
  }
  const v = gradientVector(spec.angle);
  return (
    <defs>
      <linearGradient id={id} x1={v.x1} y1={v.y1} x2={v.x2} y2={v.y2}>{stops}</linearGradient>
    </defs>
  );
}

// CSS background for div-drawn layers (rects, pills): an SVG of the same
// unit-box gradient, so diagonals match the SVG/fabric versions exactly.
export function cssGradient(spec) {
  const v = gradientVector(spec.angle);
  const grad =
    spec.kind === "radial"
      ? `<radialGradient id="g" cx=".5" cy=".5" r=".5">`
      : `<linearGradient id="g" x1="${v.x1}" y1="${v.y1}" x2="${v.x2}" y2="${v.y2}">`;
  const close = spec.kind === "radial" ? "</radialGradient>" : "</linearGradient>";
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1" preserveAspectRatio="none"><defs>${grad}` +
    `<stop offset="0" stop-color="${spec.color_start}"/><stop offset="1" stop-color="${spec.color_end}"/>${close}</defs>` +
    `<rect width="1" height="1" fill="url(#g)"/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") center/100% 100% no-repeat`;
}

export const DASH_STYLES = ["solid", "dashed", "dotted"];

// fabric strokeDashArray (+ line cap) for a dash style.
export function fabricDash(style, width) {
  const w = Math.max(Number(width) || 1, 1);
  if (style === "dashed") return { strokeDashArray: [w * 3, w * 2], strokeLineCap: "butt" };
  if (style === "dotted") return { strokeDashArray: [0, w * 2], strokeLineCap: "round" };
  return { strokeDashArray: null, strokeLineCap: "butt" };
}

export function svgDashProps(style, width) {
  const w = Math.max(Number(width) || 1, 1);
  if (style === "dashed") return { strokeDasharray: `${w * 3} ${w * 2}` };
  if (style === "dotted") return { strokeDasharray: `0 ${w * 2}`, strokeLinecap: "round" };
  return {};
}

// CSS mask that clips a box to a shape (image frames in thumbnails).
export function maskCss(kind, customPath = null) {
  if (kind === "pill" && !customPath) return { borderRadius: 9999 };
  let body;
  // Custom frame (union/intersection, frameGeometry.js): its own path.
  if (customPath) body = `<path d="${customPath}" fill-rule="evenodd"/>`;
  else if (kind === "square") body = '<rect width="100" height="100"/>';
  else if (kind === "circle") body = '<ellipse cx="50" cy="50" rx="50" ry="50"/>';
  else if (SHAPE_PATHS[kind]) body = `<path d="${SHAPE_PATHS[kind]}"/>`;
  else body = `<polygon points="${SHAPE_POINTS[kind] || SHAPE_POINTS.diamond}"/>`;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="none">${body}</svg>`;
  const url = `url("data:image/svg+xml,${encodeURIComponent(svg)}") center/100% 100% no-repeat`;
  return { WebkitMask: url, mask: url };
}
